import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowRightLeft, Building2, ChevronRight, Loader2, PackagePlus, Plus, Warehouse, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useSpares } from "@/features/spares/api";
import { SpareKpis, type SpareKpiKey } from "@/features/spares/SpareKpis";
import { ReorderAlertsCard } from "@/features/spares/ReorderAlertsCard";
import { StockBadge } from "@/features/spares/badges";
import { SpareTransferDialog } from "@/features/spares/SpareTransferDialog";
import { useAssetsInStore } from "@/features/store-indents/api";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { ListExportButtons } from "@/features/reports/ListExportButtons";
import { canCreatePurchaseIndent, canExportSpareReports } from "@/lib/roles";
import { useStores, useStoresByBranch } from "@/features/masters/api";
import type { Store } from "@/features/masters/api";
import { useBranch } from "@/context/BranchContext";
import { formatDate } from "@/features/assets/format";
import { ReplenishmentDialog } from "@/features/spares/ReplenishmentDialog";
import { hasPermission } from "@/lib/permissions";


const WRITE_ROLES = new Set(["super_admin", "biomed_head", "purchase_stores"]);
const TRANSFER_ROLES = new Set(["super_admin", "biomed_head", "facility_manager", "purchase_stores"]);

type TabKey = "all" | "spare" | "consumable" | "stationery" | "asset";
const TAB_KEYS: readonly TabKey[] = ["all", "spare", "consumable", "stationery", "asset"] as const;

export const Route = createFileRoute("/_authenticated/spares/")({
  component: SparesIndex,
  validateSearch: (s: Record<string, unknown>) => ({ type: (s.type as string) || "all", stock: s.stock as string | undefined }),
});

// ── Store selector panel ─────────────────────────────────────────────────────
function StoreSelectorPanel({
  stores,
  selectedId,
  onSelect,
}: {
  stores: Store[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-muted-foreground">Select a store to view its inventory</p>
        {selectedId && (
          <Button size="sm" variant="ghost" onClick={() => onSelect(null)}>
            <X className="mr-1 h-3 w-3" /> Show all stores
          </Button>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {stores.map((s) => (
          <button
            key={s.id}
            onClick={() => onSelect(s.id === selectedId ? null : s.id)}
            className={`group text-left rounded-xl border p-4 transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/50 ${
              s.id === selectedId
                ? "border-primary bg-primary/5 shadow-sm ring-2 ring-primary/30"
                : "border-border bg-card hover:border-primary/40"
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className={`rounded-lg p-2 shrink-0 ${s.id === selectedId ? "bg-primary/10" : "bg-muted"}`}>
                  <Warehouse className={`h-4 w-4 ${s.id === selectedId ? "text-primary" : "text-muted-foreground"}`} />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{s.name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{s.code}</p>
                </div>
              </div>
              <ChevronRight className={`h-4 w-4 shrink-0 mt-1 transition-transform ${s.id === selectedId ? "text-primary rotate-90" : "text-muted-foreground group-hover:translate-x-0.5"}`} />
            </div>
            {s.branch_name && (
              <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                <Building2 className="h-3 w-3" />
                <span className="truncate">{s.branch_name}</span>
              </div>
            )}
            <div className="mt-2">
              <Badge variant={s.is_active ? "default" : "secondary"} className="text-[10px]">
                {s.is_active ? "Active" : "Inactive"}
              </Badge>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
function SparesIndex() {
  const navigate = useNavigate();
  const { data: me } = useCurrentUser();
  const { type: initType, stock } = Route.useSearch();
  const [itemType, setItemType] = useState<TabKey>(
    TAB_KEYS.includes(initType as TabKey) ? (initType as TabKey) : "all",
  );
  const [search, setSearch] = useState("");
  const [kpiFilter, setKpiFilter] = useState<SpareKpiKey | null>(stock === "low" || stock === "out" || stock === "reorder" ? (stock as SpareKpiKey) : null);
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);

  const isAssetTab = itemType === "asset";
  const spareItemType: "all" | "spare" | "consumable" | "stationery" = isAssetTab ? "all" : itemType;

  const { currentBranchId, lockedBranchId } = useBranch();
  const effectiveBranchId = lockedBranchId ?? currentBranchId ?? null;

  const { data: allStores = [], isLoading: storesLoading } = useStoresByBranch(effectiveBranchId);
  const { data: rows = [], isLoading } = useSpares({ search, activeOnly: true, itemType: spareItemType, storeId: selectedStoreId ?? undefined, branchId: effectiveBranchId });
  const { data: assetRows = [], isLoading: assetsLoading } = useAssetsInStore({ storeId: selectedStoreId ?? undefined, branchId: effectiveBranchId });
  const canCreate = (me?.roles ?? []).some((r) => WRITE_ROLES.has(r));
  const canReplenish = hasPermission(me, "create_store_replenishment_indent");   // RBAC permission, not a role name
  const [replenishOpen, setReplenishOpen] = useState(false);
  const canTransfer = (me?.roles ?? []).some((r) => TRANSFER_ROLES.has(r));
  const [transferSpareId, setTransferSpareId] = useState<string | null>(null);

  const selectedStore = useMemo(
    () => allStores.find((s) => s.id === selectedStoreId) ?? null,
    [allStores, selectedStoreId],
  );

  const filteredRows = useMemo(() => {
    if (!kpiFilter || kpiFilter === "active") return rows;
    if (kpiFilter === "low") return rows.filter((s) => {
      const displayQty = s.store_stock ?? s.stock_total ?? 0;
      return displayQty <= s.min_qty && displayQty > 0;
    });
    if (kpiFilter === "out") return rows.filter((s) => {
      const displayQty = s.store_stock ?? s.stock_total ?? 0;
      return displayQty <= 0;
    });
    if (kpiFilter === "reorder") return rows.filter((s) => {
      const displayQty = s.store_stock ?? s.stock_total ?? 0;
      return displayQty <= s.min_qty;
    });
    return rows;
  }, [rows, kpiFilter]);

  const filteredAssets = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return assetRows;
    return assetRows.filter((a) =>
      [a.asset_code, a.name, a.make, a.model, a.serial_number]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s)),
    );
  }, [assetRows, search]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Store Items</h1>
          <p className="text-sm text-muted-foreground">
            Spares, consumables and unallocated assets stocked in the store — receipts, issues and allocation requests.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAssetTab ? (
            <Button onClick={() => navigate({ to: "/store-indents/new", search: { kind: "asset" } })}>
              <PackagePlus className="mr-1 h-4 w-4" />Raise Allocation Request
            </Button>
          ) : (
            <>
              <ListExportButtons
                rows={() =>
                  filteredRows.map((s) => {
                    const displayQty = s.store_stock ?? s.stock_total ?? 0;
                    return {
                      Code: s.code,
                      Name: s.name,
                      Unit: s.unit,
                      Rate: Number(s.rate ?? 0),
                      "On Hand": displayQty,
                      "Min Qty": s.min_qty,
                      Status: displayQty <= 0 ? "Out" : displayQty <= s.min_qty ? "Low" : "OK",
                    };
                  })
                }
                filename="Spare_Stock_Register"
                sheetName="Spares"
                disabled={isLoading || filteredRows.length === 0}
                canExport={canExportSpareReports(me?.roles)}
              />
              {canReplenish && (
                <Button variant="outline" onClick={() => setReplenishOpen(true)}>
                  <PackagePlus className="mr-1 h-4 w-4" />Replenish low stock
                </Button>
              )}
              <Button variant="outline" onClick={() => navigate({ to: "/spares/transfers" })}>
                <ArrowRightLeft className="mr-1 h-4 w-4" />Transfers
              </Button>
              {canCreate && (
                itemType === "consumable"
                  ? <Button onClick={() => navigate({ to: "/spares/new", search: { type: "consumable" } })}><Plus className="mr-1 h-4 w-4" />Add Consumable</Button>
                  : itemType === "stationery"
                  ? <Button onClick={() => navigate({ to: "/spares/new", search: { type: "stationery" } })}><Plus className="mr-1 h-4 w-4" />Add Stationery</Button>
                  : itemType === "all"
                  ? <Button onClick={() => navigate({ to: "/spares/new", search: { type: "spare" } })}><Plus className="mr-1 h-4 w-4" />Add Item</Button>
                  : <Button onClick={() => navigate({ to: "/spares/new", search: { type: "spare" } })}><Plus className="mr-1 h-4 w-4" />New Spare</Button>
              )}
            </>
          )}
        </div>
      </div>

      {!storesLoading && allStores.length === 0 ? (
        <div className="mt-8 flex flex-col items-center justify-center p-12 border rounded-xl bg-card border-dashed">
          <Warehouse className="h-12 w-12 text-muted-foreground mb-4 opacity-20" />
          <h3 className="text-lg font-medium text-foreground">No Stores Available</h3>
          <p className="text-sm text-muted-foreground mt-2 max-w-sm text-center">
            You do not have any stores assigned to your department. Please contact the administrator to request access.
          </p>
        </div>
      ) : (
        <>
      {/* Store selector */}
      {storesLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading stores…
        </div>
      ) : allStores.length > 0 ? (
        <StoreSelectorPanel
          stores={allStores}
          selectedId={selectedStoreId}
          onSelect={setSelectedStoreId}
        />
      ) : null}

      {/* Active store banner */}
      {selectedStore && (
        <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5">
          <Warehouse className="h-4 w-4 text-primary shrink-0" />
          <span className="text-sm font-medium">Viewing:</span>
          <span className="text-sm text-primary font-semibold">{selectedStore.name}</span>
          <span className="text-xs text-muted-foreground ml-1">[{selectedStore.code}]</span>
          <Button size="sm" variant="ghost" className="ml-auto h-6 px-2 text-xs" onClick={() => setSelectedStoreId(null)}>
            <X className="h-3 w-3 mr-1" />Clear
          </Button>
        </div>
      )}

      <Tabs value={itemType} onValueChange={(v) => setItemType(v as TabKey)}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="spare">Spares</TabsTrigger>
          <TabsTrigger value="consumable">Consumables</TabsTrigger>
          <TabsTrigger value="stationery">Stationery</TabsTrigger>
          <TabsTrigger value="asset">Assets</TabsTrigger>
        </TabsList>
      </Tabs>

      {!isAssetTab && (
        <>
          <SpareKpis activeKey={kpiFilter} onSelect={setKpiFilter} itemType={spareItemType} storeId={selectedStoreId} />
          <ReorderAlertsCard itemType={spareItemType} storeId={selectedStoreId} />
        </>
      )}

      <Card className="mb-4"><CardContent className="p-3">
        <Input
          className="w-64"
          placeholder={itemType === "asset" ? "Search by code, name, serial…" : "Search by code or name…"}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </CardContent></Card>

      {(itemType === "all" || itemType === "spare" || itemType === "consumable" || itemType === "stationery") && (
        <Card className="mb-4"><CardContent className="p-0">
          {isLoading ? <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div> :
            filteredRows.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No spares/consumables{selectedStore ? ` in ${selectedStore.name}` : ""}.</div> : (
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Code</TableHead><TableHead>Name</TableHead>
                  <TableHead>Unit</TableHead><TableHead>Rate (₹)</TableHead>
                  <TableHead>On Hand</TableHead><TableHead>Min</TableHead><TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {filteredRows.map((s) => {
                    const displayQty = s.store_stock ?? s.stock_total ?? 0;
                    const isLow = Number(displayQty) <= Number(s.min_qty ?? 0);
                    return (
                      <TableRow key={s.id} className="cursor-pointer" onClick={() => navigate({ to: "/spares/$spareId", params: { spareId: s.id } })}>
                        <TableCell className="font-mono text-xs">
                          <Link to="/spares/$spareId" params={{ spareId: s.id }} className="text-primary hover:underline">{s.code}</Link>
                        </TableCell>
                        <TableCell>{s.name}</TableCell>
                        <TableCell>{s.unit}</TableCell>
                        <TableCell>{Number(s.rate).toFixed(2)}</TableCell>
                        <TableCell>{displayQty}</TableCell>
                        <TableCell>{s.min_qty}</TableCell>
                        <TableCell><StockBadge qty={displayQty} min={s.min_qty} /></TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            {isLow && canCreatePurchaseIndent(me?.roles) && (
                              <Button size="sm" variant="outline" onClick={() => navigate({ to: "/indents/new", search: { kind: s.item_type === "consumable" || s.item_type === "stationery" ? s.item_type : "spare", itemId: s.id } })}>
                                Raise Indent
                              </Button>
                            )}
                            {canTransfer && (
                              <Button size="sm" variant="ghost" onClick={() => setTransferSpareId(s.id)}>
                                <ArrowRightLeft className="mr-1 h-3 w-3" />Transfer
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
        </CardContent></Card>
      )}

      {(itemType === "all" || itemType === "asset") && (
        <Card><CardContent className="p-0">
          {assetsLoading ? (
            <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : filteredAssets.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No unallocated assets in the store.</div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Asset Code</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Make · Model</TableHead>
                <TableHead>Serial</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Received</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filteredAssets.map((a) => {
                  const cat = (a as { category?: { name?: string } }).category;
                  return (
                    <TableRow key={a.id}>
                      <TableCell className="font-mono text-xs">
                        <Link to="/assets/$assetId" params={{ assetId: a.id }} className="text-primary hover:underline">
                          {a.asset_code}
                        </Link>
                      </TableCell>
                      <TableCell>{a.name}</TableCell>
                      <TableCell>{[a.make, a.model].filter(Boolean).join(" · ") || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{a.serial_number ?? "—"}</TableCell>
                      <TableCell>{cat?.name ?? "—"}</TableCell>
                      <TableCell><Badge variant="secondary">{a.status}</Badge></TableCell>
                      <TableCell>{formatDate(a.created_at)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent></Card>
      )}
        </>
      )}

      {transferSpareId && (
        <SpareTransferDialog
          spareId={transferSpareId}
          open={!!transferSpareId}
          onOpenChange={(v) => { if (!v) setTransferSpareId(null); }}
        />
      )}

      {canReplenish && <ReplenishmentDialog open={replenishOpen} onOpenChange={setReplenishOpen} branchId={effectiveBranchId} />}
    </div>
  );
}
